import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Dialog,
  Slide,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { Helmet } from "react-helmet-async";
import { toast } from "../../../utilities/SnackBarUtils";

// Slide-up transition for full-screen dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// --- Formatting helpers ---
const fmtDateLine = (dateISO) => {
  if (!dateISO) return "";
  try {
    const d = new Date(dateISO);
    return new Intl.DateTimeFormat(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
    }).format(d);
  } catch {
    return dateISO;
  }
};

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "2-digit",
    }).format(d);
  } catch {
    return "";
  }
};

// Slugify helper for canonical URLs
const slugify = (s = "") =>
  encodeURIComponent(
    s
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
  );

const Event = () => {
  const { slug, eventId } = useParams();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [channel, setChannel] = useState(null);
  const [registrationLink, setRegistrationLink] = useState("");
  const [buyOpen, setBuyOpen] = useState(false);

  const dateLine = useMemo(() => fmtDateLine(event?.date || event?.start), [event]);
  const timeRange = useMemo(() => {
    const startStr = fmtTime(event?.start);
    const endStr = fmtTime(event?.end);
    if (startStr && endStr) return `${startStr} – ${endStr}`;
    return startStr || "";
  }, [event]);

  const ticketUrl = registrationLink || event?.registrationLink || event?.eventbriteUrl || "";

  const WIDGET_SRC = "https://d15o5a6138uh8e.cloudfront.net/widget.js";

  const ensureWidgetScript = () => {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') return reject(new Error('No window'));
      // If already loaded
      if (window.mountTNFTWidget) return resolve();
      // Check for an existing script tag with the same src
      const existing = Array.from(document.getElementsByTagName('script')).find(
        (s) => s.src === WIDGET_SRC
      );
      if (existing) {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', (e) => reject(e));
        return;
      }
      const script = document.createElement('script');
      script.src = WIDGET_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = (e) => reject(e);
      document.body.appendChild(script);
    });
  };

  // Mount the ticketing widget when dialog opens
  useEffect(() => {
    if (!buyOpen) return;
    const mount = async () => {
      try {
        await ensureWidgetScript();
        // Clear previous mount (if any)
        const el = document.getElementById('tnfm-widget');
        if (el) el.innerHTML = '';
        if (window.mountTNFTWidget && eventId) {
          window.mountTNFTWidget('tnfm-widget', String(eventId), '#fc3d03');
        }
      } catch (e) {
        console.log('[Event] widget load error', e);
      }
    };
    mount();
  }, [buyOpen, eventId]);

  useEffect(() => {
    if (eventId) {
      fetchEvent(eventId);
      fetchChannel(eventId);
    }
  }, [eventId]);

  const fetchEvent = async (id) => {
    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_TNFT_SERVER}/unauth/event/${id}`;
      const res = await axios(url);
      const data = res?.data;
      if (data?.success) setEvent(data.data);
      else setEvent(data);
    } catch (e) {
      console.log("[Event] fetch error", e?.message || e);
      toast.error(e?.response?.data?.error || "Unable to load event");
      setEvent(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchChannel = async (id) => {
    try {
      const url = `${process.env.REACT_APP_TNFT_SERVER}/unauth/tickets/channel/?eventId=${id}`;
      const result = await axios(url);
      const data = result?.data;
      if (data?.success) {
        setChannel(data?.channel);
        setRegistrationLink(data?.url);
      }
    } catch (e) {
      console.log("[Event] channel error", e?.message || e);
      // Non-blocking
    }
  };

  if (loading && !event) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography>Loading…</Typography>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h6" gutterBottom>
          Event not found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please check the link and try again.
        </Typography>
      </Container>
    );
  }

  // --- SEO derivations ---
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const safeSlug = slug || (event?.name ? slugify(event.name) : "event");
  const canonical = origin ? `${origin}/event/${safeSlug}/${eventId || ""}` : undefined;
  const title = event?.name
    ? `${event.name}${event?.venueCity ? " — " + event.venueCity : ""}`
    : "Event";
  const desc = event?.description
    ? event.description.replace(/\s+/g, " ").slice(0, 160)
    : (dateLine ? `${event?.name || "Event"} on ${dateLine}${timeRange ? ` · ${timeRange}` : ""}` : event?.name || "");
  const hero = event?.imageUrl;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    name: event?.name,
    description: event?.description,
    startDate: event?.start || event?.date,
    endDate: event?.end || undefined,
    eventStatus: "https://schema.org/EventScheduled",
    image: hero ? [hero] : undefined,
    location: (event?.venueName || event?.venueCity || event?.venueAddress1 || event?.VenueAddress1)
      ? {
          "@type": "Place",
          name: event?.venueName,
          address: {
            "@type": "PostalAddress",
            streetAddress: event?.venueAddress1 || event?.VenueAddress1,
            addressLocality: event?.venueCity,
          },
        }
      : undefined,
    organizer: event?.org?.name ? { "@type": "Organization", name: event.org.name } : undefined,
    offers: (event?.registrationLink || event?.eventbriteUrl)
      ? {
          "@type": "Offer",
          url: event.registrationLink || event.eventbriteUrl,
          availability: "https://schema.org/InStock",
        }
      : undefined,
  };

  return (
    <>
      <Helmet>
        {title && <title>{title}</title>}
        {desc && <meta name="description" content={desc} />}
        {canonical && <link rel="canonical" href={canonical} />}
        {hero && <link rel="preload" as="image" href={hero} />}

        {/* Open Graph */}
        {title && <meta property="og:title" content={title} />}
        {desc && <meta property="og:description" content={desc} />}        
        {canonical && <meta property="og:url" content={canonical} />}
        {hero && <meta property="og:image" content={hero} />}
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        {title && <meta name="twitter:title" content={title} />}
        {desc && <meta name="twitter:description" content={desc} />}
        {hero && <meta name="twitter:image" content={hero} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 6 }, px: { xs: 4, md: 8}, mb: 10 }}>
        {/* Back button */}
        <Box sx={{ mb: 2 }}>
          <IconButton
            onClick={() => {
              try { localStorage.setItem('tnft.returnToBrowse', '1'); } catch {}
              window.history.back();
            }}
            aria-label="Back"
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        </Box>
        {/* Top hero (2:1), full width within container */}
        {event?.imageUrl && (
          <Box sx={{ borderRadius: 4, overflow: 'hidden', mb: { xs: 2, md: 4} }}>
            <Box sx={{ aspectRatio: '2 / 1', width: '100%' }}>
              <img
                src={event.imageUrl}
                alt={event.name}
                width="1200"
                height="600"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </Box>
          </Box>
        )}

        {/* Two columns below */}
        <Grid container spacing={4} alignItems="flex-start">
          {/* Left: name & description */}
          <Grid item xs={12} md={8}>
            {dateLine && (
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                {dateLine}
                {timeRange ? ` · ${timeRange}` : ""}
              </Typography>
            )}

            <Typography variant="h4"  sx={{ fontWeight: 800, lineHeight: 1.05 }} gutterBottom>
              {event?.name}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Hosted By {event?.org?.name }
              </Typography>
            </Box>

            {event?.description && (
              <Typography variant="body1" sx={{ mt: 2, whiteSpace: "pre-wrap" }}>
                {event.description}
              </Typography>
            )}
          </Grid>

          {/* Right: venue & details (compact) */}
          <Grid item xs={12} md={4}>
            <Box sx={{ borderRadius: 3, border: 1, borderColor: 'divider', p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Event details
              </Typography>
              {(event?.venueName || event?.VenueAddress1 || event?.venueCity) && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Venue
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {event?.venueName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[event?.venueAddress1, event?.venueCity].filter(Boolean).join(', ')}
                  </Typography>
                </Box>
              )}
              {(dateLine || timeRange) && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    When
                  </Typography>
                  <Typography variant="body1">
                    {dateLine} {timeRange ? `· ${timeRange}` : ''}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Floating Buy button */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 16,
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 720, px: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={(!ticketUrl && channel === 'eventbrite')}
            onClick={() => {
              if (channel !== 'tnfm' && registrationLink) {
                window.open(registrationLink, '_blank', 'noopener,noreferrer');
              } else {
                setBuyOpen(true);
              }
            }}
            sx={{ pointerEvents: "auto", py: 1, borderRadius: 9999 }}
          >
            Buy Tickets
          </Button>
        </Box>
      </Box>

      {/* Full-screen slide-up dialog */}
      <Dialog fullScreen open={buyOpen} onClose={() => setBuyOpen(false)} TransitionComponent={Transition}>
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6">Checkout</Typography>
            <IconButton onClick={() => setBuyOpen(false)} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ p: { xs: 0, md: 2 } }}>
            <Box id="tnfm-widget" sx={{ minHeight: 320 }} />
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default Event;